import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, ShoppingCart, TrendingUp, Camera, Clock, ArrowRight, Euro } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  pending: { label: 'In Attesa', color: '#D98324' },
  confirmed: { label: 'Confermato', color: '#4A6B53' },
  shipped: { label: 'Spedito', color: '#0088cc' },
  delivered: { label: 'Consegnato', color: '#2D5A3D' },
  cancelled: { label: 'Annullato', color: '#DC2626' },
};

export default function AdminDashboardPage() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const getHeaders = () => {
    const headers = {};
    if (user?.role === 'superadmin') {
      const activeShop = localStorage.getItem('phv_active_shop');
      if (activeShop) headers['X-Shop-Id'] = activeShop;
    }
    return headers;
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) { navigate('/account'); return; }
    axios.get(`${API}/admin/dashboard`, { withCredentials: true, headers: getHeaders() })
      .then(({ data }) => setData(data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, authLoading, navigate]);

  if (loading) {
    return (
      <div className="pb-safe px-4 pt-4 space-y-4">
        {[1,2,3].map(i => <div key={i} className="h-20 skeleton-shimmer rounded-xl" />)}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div data-testid="admin-dashboard-page" className="pb-safe px-4 pt-4">
      <h1 className="text-2xl font-semibold text-[#2A2624] mb-4" style={{ fontFamily: 'Outfit' }}>
        Dashboard
      </h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <StatCard icon={Package} label="Prodotti" value={data.published_products} sub={`${data.total_products} totali`} color="#C25934" />
        <StatCard icon={ShoppingCart} label="Ordini" value={data.total_orders} sub={`${data.pending_orders} in attesa`} color="#D98324" />
        <StatCard icon={Euro} label="Fatturato" value={`${data.revenue?.toFixed(0)}€`} sub="totale" color="#4A6B53" />
        <StatCard icon={Camera} label="Oggi" value={data.today_uploads} sub="caricati oggi" color="#0088cc" />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <Button
          data-testid="quick-upload"
          onClick={() => navigate('/admin')}
          className="h-12 rounded-xl bg-[#C25934] hover:bg-[#A64B2B] text-white text-sm"
        >
          <Camera size={16} className="mr-1.5" /> Carica Prodotto
        </Button>
        <Button
          data-testid="quick-orders"
          onClick={() => navigate('/ordini')}
          variant="outline"
          className="h-12 rounded-xl border-[#E8E3D9] text-[#5C554F] text-sm"
        >
          <ShoppingCart size={16} className="mr-1.5" /> Vedi Ordini
        </Button>
      </div>

      {/* Order Stats */}
      {data.order_stats && (
        <div className="bg-white rounded-xl border border-[#E8E3D9] p-4 mb-5">
          <h3 className="text-sm font-bold text-[#2A2624] uppercase tracking-wider mb-3">Ordini per Stato</h3>
          <div className="space-y-2">
            {Object.entries(data.order_stats).map(([key, count]) => {
              const cfg = STATUS_LABELS[key];
              const total = data.total_orders || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={key} className="flex items-center gap-3">
                  <span className="text-xs w-20 text-[#5C554F]">{cfg?.label || key}</span>
                  <div className="flex-1 h-2 bg-[#F0EBE1] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: cfg?.color || '#877E78' }} />
                  </div>
                  <span className="text-xs font-bold w-8 text-right" style={{ color: cfg?.color }}>{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Orders */}
      {data.recent_orders?.length > 0 && (
        <div className="bg-white rounded-xl border border-[#E8E3D9] p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-[#2A2624] uppercase tracking-wider">Ultimi Ordini</h3>
            <button onClick={() => navigate('/ordini')} className="flex items-center gap-0.5 text-xs text-[#C25934] font-medium">
              Tutti <ArrowRight size={12} />
            </button>
          </div>
          <div className="space-y-2">
            {data.recent_orders.map(o => {
              const cfg = STATUS_LABELS[o.status];
              return (
                <div key={o.id} className="flex items-center justify-between py-2 border-b border-[#E8E3D9] last:border-0">
                  <div>
                    <p className="text-sm font-medium text-[#2A2624]">{o.customer_name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${cfg?.color}15`, color: cfg?.color }}>
                        {cfg?.label}
                      </span>
                      <span className="text-[10px] text-[#877E78]">
                        {new Date(o.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
                      </span>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-[#C25934]">{o.total?.toFixed(2)} &euro;</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Share Today's Flyer */}
      {data.today_flyer_id && (
        <Button
          data-testid="share-today-flyer"
          onClick={() => navigate(`/volantino/${data.today_flyer_id}`)}
          className="w-full h-12 rounded-xl bg-[#D98324] hover:bg-[#C47720] text-white mt-4"
        >
          <TrendingUp size={16} className="mr-1.5" /> Condividi Volantino di Oggi ({data.today_uploads} prodotti)
        </Button>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white rounded-xl border border-[#E8E3D9] p-3">
      <Icon size={18} style={{ color }} />
      <p className="text-2xl font-bold text-[#2A2624] mt-1" style={{ fontFamily: 'Outfit' }}>{value}</p>
      <p className="text-xs text-[#877E78]">{label}</p>
      {sub && <p className="text-[10px] text-[#877E78]">{sub}</p>}
    </div>
  );
}
